/**
 * Text layout inside a subtitle layer box (preview + export font scaling).
 */

import {
  subtitlePreviewFontPx,
  subtitlePreviewTextShadow,
  subtitleCharsPerLineForBox,
  splitSubtitlePreviewLines,
  subtitleLinesIncludeFullText,
  subtitleLinesOverflowChars,
  SUBTITLE_EXPORT_REF_HEIGHT,
  SUBTITLE_MAX_FONT_SIZE,
  SUBTITLE_MAX_PREVIEW_FONT_PX,
  SUBTITLE_MIN_FONT_SIZE,
} from '@/lib/videoSubtitlePreview';
import { normalizedLayerToPixelRect } from '@/lib/subtitles/subtitleCoordinates';
import { resolveSubtitleFontFamily } from '@/lib/subtitles/subtitleStyleConfig';

export const SUBTITLE_MIN_PREVIEW_FONT_PX = 9;
export { SUBTITLE_MAX_PREVIEW_FONT_PX };

/** Fit export font size to layer height (1080p reference). */
export function fontSizeFromLayerHeight(layerHeightNorm, exportRefH = SUBTITLE_EXPORT_REF_HEIGHT) {
  const h = Math.max(0.06, Number(layerHeightNorm) || 0.14);
  const base = 22;
  const px = Math.round((h * exportRefH) * (base / (SUBTITLE_EXPORT_REF_HEIGHT * 0.14)));
  return Math.min(SUBTITLE_MAX_FONT_SIZE, Math.max(SUBTITLE_MIN_FONT_SIZE, px));
}

export function computeSubtitleLayerTextLayout({
  text,
  layer,
  style,
  frameW,
  frameH,
  aspectRatio,
  viewportMin,
}) {
  const rect = normalizedLayerToPixelRect(layer, frameW, frameH);
  const boxW = Math.max(32, rect.width - 8);
  const boxH = Math.max(20, rect.height - 4);
  const displayText = String(text || '').normalize('NFC');
  const resolvedStyle = {
    ...style,
    fontFamily: resolveSubtitleFontFamily(displayText, style?.fontFamily),
  };

  let fontPx = subtitlePreviewFontPx(
    resolvedStyle.fontSize,
    frameW,
    frameH,
    viewportMin,
    aspectRatio,
  );

  const portrait = String(aspectRatio).includes('9:16') || String(aspectRatio).includes('4:5');
  const lineHeightFactor = 1.28;
  let lines = [];
  let charsPerLine = 24;
  let maxLines = 2;

  for (let attempt = 0; attempt < 16; attempt += 1) {
    const maxFromBoxH = (boxH / lineHeightFactor) * (resolvedStyle.bold ? 0.92 : 1);
    fontPx = Math.min(fontPx, maxFromBoxH);
    const maxFromBoxW = boxW / 7.5;
    fontPx = Math.min(fontPx, maxFromBoxW);
    fontPx = Math.max(SUBTITLE_MIN_PREVIEW_FONT_PX, Math.min(SUBTITLE_MAX_PREVIEW_FONT_PX, fontPx));

    maxLines = Math.max(
      1,
      Math.min(portrait ? 10 : 12, Math.floor(boxH / (fontPx * lineHeightFactor))),
    );
    charsPerLine = subtitleCharsPerLineForBox(boxW, fontPx);
    lines = splitSubtitlePreviewLines(displayText, maxLines, charsPerLine, {
      truncateOverflow: false,
    });

    const fitsVertically = lines.length <= maxLines;
    const fitsHorizontally = !subtitleLinesOverflowChars(lines, charsPerLine);
    const hasFullText = subtitleLinesIncludeFullText(displayText, lines);
    if (fitsVertically && fitsHorizontally && hasFullText) {
      break;
    }
    fontPx = Math.max(SUBTITLE_MIN_PREVIEW_FONT_PX, fontPx * 0.9);
  }

  const align = layer?.textAlign || 'center';
  const shadow = subtitlePreviewTextShadow(
    resolvedStyle,
    frameH,
    viewportMin,
    aspectRatio,
    frameW,
  );

  return {
    rect,
    fontPx,
    fontPxExact: fontPx,
    lines,
    maxLines,
    textAlign: align,
    shadow,
    boxW,
    boxH,
  };
}
